/**
 * StatusToggle — die zentrale Statuseingabe (Wunsch/Optional/Verhindert).
 *
 * Exklusive, volle-Breite `ToggleButtonGroup` mit drei `ToggleButton`s (Icon
 * über Text, Mindesthöhe 64 px). Die Farbe/das Icon kommen aus `STATUS_META`,
 * der Text aus dem i18n-Katalog. Erneutes Antippen des aktiven Werts setzt
 * zurück (MUI liefert dann `null` an `onChange`).
 *
 * Status wird nie nur über Farbe gezeigt — jeder Button trägt Icon UND Text
 * (Req 2.2).
 */

import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import { useI18n } from "../../i18n/I18nProvider";
import { STATUS_META, type AvailabilityStatus } from "../../theme/status";

const ORDER: AvailabilityStatus[] = ["wish", "optional", "blocker"];

export default function StatusToggle({
  value,
  ariaLabel,
  disabled,
  onChange,
}: {
  /** Aktueller Status oder null (keine Angabe). */
  value: AvailabilityStatus | null;
  /** aria-label der Gruppe (Req: Statusgruppe mit aria-label). */
  ariaLabel: string;
  disabled?: boolean;
  /** `null` = zurücksetzen (erneutes Tippen auf den aktiven Wert). */
  onChange: (next: AvailabilityStatus | null) => void;
}) {
  const { t } = useI18n();

  return (
    <ToggleButtonGroup
      exclusive
      fullWidth
      value={value}
      disabled={disabled}
      onChange={(_e, next: AvailabilityStatus | null) => onChange(next)}
      aria-label={ariaLabel}
      sx={{
        gap: 0.75,
        "& .MuiToggleButtonGroup-grouped": {
          border: "2px solid",
          borderColor: "divider",
          borderRadius: "14px !important",
          m: 0,
        },
      }}
    >
      {ORDER.map((s) => {
        const meta = STATUS_META[s];
        const Icon = meta.icon;
        return (
          <ToggleButton
            key={s}
            value={s}
            color={meta.color}
            sx={{
              flexDirection: "column",
              gap: 0.25,
              minHeight: 64,
              px: 0.5,
              lineHeight: 1.15,
              fontSize: 13,
              fontWeight: 500,
              "&.Mui-selected": {
                borderColor: `${meta.color}.main`,
                fontWeight: 700,
              },
            }}
          >
            <Icon fontSize="small" />
            {t(meta.labelKey)}
          </ToggleButton>
        );
      })}
    </ToggleButtonGroup>
  );
}
